// `/recepcao` (5.6): leitura da pulseira + cadastro do paciente e lista "Cadastrados hoje".
import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ClipboardList, MessageSquareText, Plus, RefreshCw, Save, UserRound } from "lucide-react";
import {
  formatBrazilianPhone,
  normalizeBrazilianPhone,
  onlyDigits,
  patientInitials,
  type DeviceSession,
  type PatientRegistration,
  type RegistrationLanguage
} from "@centro-cirurgico/contracts";
import { Badge, Empty, Notice, formatTime } from "@centro-cirurgico/ui";

import { DeviceShell } from "../components/DeviceShell";
import { RequireDeviceSession } from "../components/RequireDeviceSession";
import { WristbandScanner, type WristbandScannerHandle } from "../components/WristbandScanner";
import type { ScanFeedback } from "../components/scan-actions";
import {
  createRegistration,
  errorMessage,
  getRegistrationByCode,
  isHttpError,
  listRegistrations,
  sendTestMessage,
  updateRegistration
} from "../utils/api";

const REFRESH_MS = 30_000;

interface FormState {
  id: string | null;
  wristbandCode: string;
  patientName: string;
  companionPhone: string;
  patientPhone: string;
  language: RegistrationLanguage;
}

interface FormErrors {
  wristbandCode?: string;
  patientName?: string;
  companionPhone?: string;
  patientPhone?: string;
}

interface NoticeState {
  error?: string | null;
  success?: string | null;
  warning?: string | null;
}

const EMPTY_FORM: FormState = {
  id: null,
  wristbandCode: "",
  patientName: "",
  companionPhone: "",
  patientPhone: "",
  language: "pt-BR"
};

/** Data local em `YYYY-MM-DD` (o dia da recepção, não UTC). */
function localDate(date = new Date()): string {
  const pad = (value: number) => String(value).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/** Máscara progressiva `(00) 00000-0000` / `(00) 0000-0000`. */
export function maskPhone(value: string): string {
  let digits = onlyDigits(value);
  if (digits.startsWith("55") && digits.length > 11) digits = digits.slice(2);
  digits = digits.slice(0, 11);
  if (digits.length === 0) return "";
  if (digits.length <= 2) return `(${digits}`;
  if (digits.length <= 6) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
  if (digits.length <= 10) return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
  return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
}

function formFromRegistration(registration: PatientRegistration): FormState {
  return {
    id: registration.id,
    wristbandCode: registration.wristbandCode,
    patientName: registration.patientName,
    companionPhone: formatBrazilianPhone(registration.companionPhone),
    patientPhone: formatBrazilianPhone(registration.patientPhone),
    language: registration.language
  };
}

function flowLabel(registration: PatientRegistration): string {
  switch (registration.flow) {
    case "in_room":
      return registration.currentRoomName ? `Em sala (${registration.currentRoomName})` : "Em sala";
    case "exited":
      return "Saiu";
    default:
      return "Aguardando entrada";
  }
}

export function ReceptionPage() {
  return (
    <RequireDeviceSession mode="reception">{(session) => <ReceptionScreen session={session} />}</RequireDeviceSession>
  );
}

function ReceptionScreen({ session }: { session: DeviceSession }) {
  const queryClient = useQueryClient();
  const today = localDate();
  const scannerRef = useRef<WristbandScannerHandle | null>(null);
  const nameInputRef = useRef<HTMLInputElement | null>(null);
  const [form, setForm] = useState<FormState | null>(null);
  const [errors, setErrors] = useState<FormErrors>({});
  const [notice, setNotice] = useState<NoticeState>({});
  const [saving, setSaving] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);

  const registrationsQuery = useQuery({
    queryKey: ["reception", "registrations", today],
    queryFn: () => listRegistrations(today),
    refetchInterval: REFRESH_MS
  });

  const refreshList = useCallback(
    () => queryClient.invalidateQueries({ queryKey: ["reception", "registrations"] }),
    [queryClient]
  );

  const openRegistration = useCallback((registration: PatientRegistration) => {
    setForm(formFromRegistration(registration));
    setErrors({});
    setNotice({});
  }, []);

  const openNew = useCallback((code = "") => {
    setForm({ ...EMPTY_FORM, wristbandCode: code });
    setErrors({});
    setNotice({});
  }, []);

  // Foco no nome ao abrir o formulário (cadastro novo ou edição).
  const formKey = form ? `${form.id ?? "new"}:${form.wristbandCode}` : null;
  useEffect(() => {
    if (formKey) nameInputRef.current?.focus();
  }, [formKey]);

  const handleScan = useCallback(
    async (code: string): Promise<ScanFeedback> => {
      const found = await getRegistrationByCode(code);
      if (found) {
        openRegistration(found);
        return {
          tone: "info",
          title: "Cadastro encontrado",
          detail: `${patientInitials(found.patientName)} · ${found.wristbandCode} — confira e salve as alterações.`
        };
      }
      openNew(code);
      return { tone: "ok", title: "Pulseira lida", detail: `Código ${code}. Preencha os dados do paciente.` };
    },
    [openNew, openRegistration]
  );

  function update<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((current) => (current ? { ...current, [key]: value } : current));
    setErrors((current) => ({ ...current, [key]: undefined }));
  }

  function validate(state: FormState): FormErrors {
    const next: FormErrors = {};
    if (state.wristbandCode.trim().length < 2) next.wristbandCode = "Informe o código da pulseira.";
    if (state.patientName.trim().length < 3) next.patientName = "Informe o nome completo do paciente.";
    if (state.companionPhone.trim() && !normalizeBrazilianPhone(state.companionPhone)) {
      next.companionPhone = "WhatsApp inválido. Use DDD + número (10 ou 11 dígitos).";
    }
    if (state.patientPhone.trim() && !normalizeBrazilianPhone(state.patientPhone)) {
      next.patientPhone = "WhatsApp inválido. Use DDD + número (10 ou 11 dígitos).";
    }
    return next;
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!form || saving) return;
    const validation = validate(form);
    setErrors(validation);
    if (Object.keys(validation).length > 0) {
      setNotice({ error: "Corrija os campos destacados." });
      return;
    }
    setSaving(true);
    setNotice({});
    const companionPhone = normalizeBrazilianPhone(form.companionPhone);
    const patientPhone = normalizeBrazilianPhone(form.patientPhone);
    try {
      const saved = form.id
        ? await updateRegistration(form.id, {
            patientName: form.patientName.trim(),
            companionPhone,
            patientPhone,
            language: form.language
          })
        : await createRegistration({
            wristbandCode: form.wristbandCode.trim(),
            patientName: form.patientName.trim(),
            companionPhone,
            patientPhone,
            language: form.language
          });
      setForm(formFromRegistration(saved));
      setNotice({
        success: form.id ? "Cadastro atualizado." : "Cadastro salvo. O paciente já pode entrar na sala.",
        warning: saved.companionPhone ? null : "Sem WhatsApp do acompanhante: nenhuma mensagem será enviada."
      });
      void refreshList();
    } catch (error) {
      if (isHttpError(error) && error.code === "registration_code_exists") {
        setNotice({ error: `${error.message} Leia a pulseira novamente para abrir o cadastro existente.` });
      } else if (isHttpError(error) && error.issues.length > 0) {
        const fieldErrors: FormErrors = {};
        for (const issue of error.issues) {
          if (issue.path === "companionPhone") fieldErrors.companionPhone = issue.message;
          else if (issue.path === "patientPhone") fieldErrors.patientPhone = issue.message;
          else if (issue.path === "patientName") fieldErrors.patientName = issue.message;
          else if (issue.path === "wristbandCode") fieldErrors.wristbandCode = issue.message;
        }
        setErrors(fieldErrors);
        setNotice({ error: error.message });
      } else {
        setNotice({ error: errorMessage(error, "Não foi possível salvar o cadastro.") });
      }
    } finally {
      setSaving(false);
    }
  }

  async function handleTestMessage() {
    if (!form?.id || sendingTest) return;
    setSendingTest(true);
    setNotice({});
    try {
      const response = await sendTestMessage(form.id);
      setNotice(response.queued ? { success: response.message } : { warning: response.message });
    } catch (error) {
      setNotice({ error: errorMessage(error, "Não foi possível enviar a mensagem de teste.") });
    } finally {
      setSendingTest(false);
    }
  }

  const registrations = registrationsQuery.data;
  const sorted = useMemo(
    () => [...(registrations ?? [])].sort((a, b) => b.createdAt.localeCompare(a.createdAt)),
    [registrations]
  );
  const canTest = Boolean(form?.id && normalizeBrazilianPhone(form.companionPhone));
  const companionMissing = Boolean(form && !form.companionPhone.trim());

  return (
    <DeviceShell tag="RECEPÇÃO" title={session.hospitalName} subtitle={session.label}>
      <div className="reception-grid">
        <section className="scan-panel reception-scan" aria-labelledby="reception-scan-title">
          <h2 id="reception-scan-title">Ler pulseira</h2>
          <p>Leia o código da pulseira para cadastrar o paciente ou abrir um cadastro existente.</p>
          <WristbandScanner
            ref={scannerRef}
            onScan={handleScan}
            idleTitle="Pronto para ler"
            idleDetail="Aproxime a pulseira da câmera, use o leitor ou digite o código abaixo."
            manualLabel="Código da pulseira"
            manualButtonLabel="Buscar cadastro"
          />
          {!form && (
            <button type="button" className="secondary" onClick={() => openNew()}>
              <Plus aria-hidden="true" size={18} /> Cadastrar sem leitura
            </button>
          )}
        </section>

        <section className="login-card reception-form" aria-labelledby="reception-form-title">
          <span className="eyebrow">
            <UserRound aria-hidden="true" size={14} /> Cadastro do paciente
          </span>
          <h2 id="reception-form-title">{form?.id ? "Editar cadastro" : "Novo cadastro"}</h2>
          {form ? (
            <form className="form-grid" onSubmit={(event) => void handleSubmit(event)} noValidate>
              <label className="span-2" htmlFor="reg-name">
                Nome do paciente
                <input
                  id="reg-name"
                  ref={nameInputRef}
                  type="text"
                  autoComplete="off"
                  required
                  value={form.patientName}
                  onChange={(event) => update("patientName", event.target.value)}
                  aria-invalid={errors.patientName ? true : undefined}
                  aria-describedby={errors.patientName ? "reg-name-error" : undefined}
                />
                {errors.patientName && (
                  <span id="reg-name-error" className="field-error">
                    {errors.patientName}
                  </span>
                )}
              </label>

              <label htmlFor="reg-code">
                Código da pulseira
                <input
                  id="reg-code"
                  type="text"
                  autoComplete="off"
                  autoCapitalize="characters"
                  required
                  readOnly={Boolean(form.id)}
                  value={form.wristbandCode}
                  onChange={(event) => update("wristbandCode", event.target.value)}
                  aria-invalid={errors.wristbandCode ? true : undefined}
                  aria-describedby={errors.wristbandCode ? "reg-code-error" : "reg-code-hint"}
                />
                {errors.wristbandCode ? (
                  <span id="reg-code-error" className="field-error">
                    {errors.wristbandCode}
                  </span>
                ) : (
                  <small id="reg-code-hint">
                    {form.id ? "O código não pode ser alterado após o cadastro." : "Leitura ou digitação."}
                  </small>
                )}
              </label>

              <label htmlFor="reg-language">
                Idioma das mensagens
                <select
                  id="reg-language"
                  value={form.language}
                  onChange={(event) => update("language", event.target.value as RegistrationLanguage)}
                >
                  <option value="pt-BR">Português (Brasil)</option>
                  <option value="en">English</option>
                </select>
              </label>

              <label htmlFor="reg-companion">
                WhatsApp do acompanhante
                <input
                  id="reg-companion"
                  type="tel"
                  inputMode="tel"
                  autoComplete="off"
                  placeholder="(00) 00000-0000"
                  value={form.companionPhone}
                  onChange={(event) => update("companionPhone", maskPhone(event.target.value))}
                  aria-invalid={errors.companionPhone ? true : undefined}
                  aria-describedby={errors.companionPhone ? "reg-companion-error" : "reg-companion-hint"}
                />
                {errors.companionPhone ? (
                  <span id="reg-companion-error" className="field-error">
                    {errors.companionPhone}
                  </span>
                ) : (
                  <small id="reg-companion-hint">Obrigatório para receber as mensagens de entrada e saída.</small>
                )}
              </label>

              <label htmlFor="reg-patient-phone">
                WhatsApp do paciente (opcional)
                <input
                  id="reg-patient-phone"
                  type="tel"
                  inputMode="tel"
                  autoComplete="off"
                  placeholder="(00) 00000-0000"
                  value={form.patientPhone}
                  onChange={(event) => update("patientPhone", maskPhone(event.target.value))}
                  aria-invalid={errors.patientPhone ? true : undefined}
                  aria-describedby={errors.patientPhone ? "reg-patient-phone-error" : undefined}
                />
                {errors.patientPhone && (
                  <span id="reg-patient-phone-error" className="field-error">
                    {errors.patientPhone}
                  </span>
                )}
              </label>

              {companionMissing && !notice.warning && (
                <Notice warning="Sem WhatsApp do acompanhante, nenhuma mensagem será enviada." />
              )}
              <Notice error={notice.error} success={notice.success} warning={notice.warning} />

              <div className="form-actions">
                <button
                  type="button"
                  className="secondary"
                  onClick={() => {
                    setForm(null);
                    setNotice({});
                    scannerRef.current?.reset();
                  }}
                  disabled={saving}
                >
                  Cancelar
                </button>
                {form.id && (
                  <button
                    type="button"
                    className="secondary"
                    onClick={() => void handleTestMessage()}
                    disabled={!canTest || sendingTest || saving}
                    title={canTest ? undefined : "Salve um WhatsApp do acompanhante para testar."}
                  >
                    <MessageSquareText aria-hidden="true" size={18} />
                    {sendingTest ? "Enviando…" : "Enviar mensagem de teste"}
                  </button>
                )}
                <button type="submit" className="primary" disabled={saving} aria-busy={saving}>
                  <Save aria-hidden="true" size={18} /> {saving ? "Salvando…" : "Salvar"}
                </button>
              </div>
            </form>
          ) : (
            <Empty title="Nenhum cadastro aberto" icon={<ClipboardList size={32} />}>
              Leia a pulseira ou toque em um cadastro da lista para editar.
            </Empty>
          )}
        </section>
      </div>

      <section className="panel registrations-panel" aria-labelledby="registrations-title">
        <div className="panel-heading">
          <div>
            <h2 id="registrations-title">Cadastrados hoje</h2>
            <p>Atualiza automaticamente a cada 30 segundos. Toque em um cadastro para editar.</p>
          </div>
          <button
            type="button"
            className="icon-button"
            onClick={() => void registrationsQuery.refetch()}
            aria-label="Atualizar lista"
            disabled={registrationsQuery.isFetching}
          >
            <RefreshCw aria-hidden="true" size={18} className={registrationsQuery.isFetching ? "spin" : undefined} />
          </button>
        </div>
        {registrationsQuery.isPending ? (
          <p className="loading" role="status">
            Carregando cadastros…
          </p>
        ) : registrationsQuery.isError ? (
          <Notice error={errorMessage(registrationsQuery.error, "Não foi possível carregar os cadastros de hoje.")} />
        ) : sorted.length === 0 ? (
          <Empty title="Nenhum cadastro hoje">Os pacientes cadastrados nesta unidade aparecerão aqui.</Empty>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th scope="col">Paciente</th>
                  <th scope="col">Pulseira</th>
                  <th scope="col">Situação</th>
                  <th scope="col">Cadastro</th>
                  <th scope="col">
                    <span className="sr-only">Ações</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {sorted.map((registration) => (
                  <tr
                    key={registration.id}
                    className={form?.id === registration.id ? "selected" : undefined}
                    onClick={() => openRegistration(registration)}
                  >
                    <td>
                      <strong>{patientInitials(registration.patientName)}</strong>
                      <br />
                      <small>{registration.companionPhone ? "Acompanhante com WhatsApp" : "Sem WhatsApp"}</small>
                    </td>
                    <td>{registration.wristbandCode}</td>
                    <td>
                      <Badge value={registration.flow} label={flowLabel(registration)} />
                    </td>
                    <td>{formatTime(registration.createdAt)}</td>
                    <td>
                      <button
                        type="button"
                        className="text-link"
                        onClick={(event) => {
                          event.stopPropagation();
                          openRegistration(registration);
                        }}
                      >
                        Editar
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </DeviceShell>
  );
}
